import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Megaphone, LifeBuoy, ShieldCheck, Loader2, Send, CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { api } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const SERVICES = [
  { id: 'contact', label: 'Contact général', email: 'contact@fodindincagnotte.com', Icon: Mail, color: '#d97757', desc: 'Questions générales, partenariats, presse.' },
  { id: 'marketing', label: 'Marketing', email: 'marketing@fodindincagnotte.com', Icon: Megaphone, color: '#b45309', desc: 'Campagnes, ambassadeurs, communication.' },
  { id: 'support', label: 'Support', email: 'support@fodindincagnotte.com', Icon: LifeBuoy, color: '#5a8268', desc: 'Aide sur une cagnotte, un don ou un retrait.' },
  { id: 'securite', label: 'Sécurité', email: 'securite@fodindincagnotte.com', Icon: ShieldCheck, color: '#4A148C', desc: 'Signaler une fraude, une faille ou un abus.' },
];

export default function ContactPage() {
  const { toast } = useToast();
  const [service, setService] = useState('contact');
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim()) { toast({ title: 'Nom requis', description: 'Indiquez votre nom.' }); return; }
    if (!form.email.trim() && !form.phone.trim()) { toast({ title: 'Coordonnées requises', description: 'Indiquez un email ou un numéro pour être recontacté.' }); return; }
    if (form.message.trim().length < 5) { toast({ title: 'Message trop court', description: 'Décrivez votre demande (5 caractères min).' }); return; }
    setSending(true);
    try {
      await api.post('/contact', {
        service,
        name: form.name.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        message: form.message.trim(),
      });
      setSent(true);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || "Impossible d'envoyer le message." });
    } finally { setSending(false); }
  };

  const active = SERVICES.find((s) => s.id === service);

  return (
    <div className="bg-[#f5f4ef] min-h-screen">
      <Header onCreate={() => {}} />
      <div className="max-w-[1000px] mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="contact-page">
        <Link to="/" className="inline-flex items-center gap-1.5 text-[13px] text-neutral-600 hover:text-black">
          <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
        </Link>

        <div className="mt-6">
          <div className="text-[11px] tracking-[0.18em] uppercase font-medium text-neutral-500">Fodindin</div>
          <h1 className="text-[26px] sm:text-[32px] font-semibold text-[#1a1d1a]">Contactez-nous</h1>
          <p className="mt-2 text-[14.5px] text-neutral-600 max-w-xl">Choisissez le service concerné : votre message sera routé vers la bonne équipe et traité rapidement.</p>
        </div>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {SERVICES.map(({ id, label, email, Icon, color, desc }) => (
            <button
              key={id}
              type="button"
              data-testid={`contact-service-${id}`}
              onClick={() => setService(id)}
              className={`text-left rounded-2xl border p-4 bg-white transition-all ${service === id ? 'border-[#1a1d1a] shadow-md -translate-y-0.5' : 'border-black/5 hover:border-black/20'}`}
            >
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}18`, color }}>
                <Icon className="w-4.5 h-4.5" />
              </div>
              <div className="mt-2.5 text-[14px] font-semibold text-[#1a1d1a]">{label}</div>
              <div className="mt-0.5 text-[11.5px] text-neutral-500 leading-snug">{desc}</div>
              <div className="mt-2 text-[11px] font-mono break-all" style={{ color }}>{email}</div>
            </button>
          ))}
        </div>

        <div className="mt-8 rounded-3xl bg-white border border-black/5 p-6 sm:p-8 max-w-[640px]">
          {!sent ? (
            <>
              <div className="flex items-center gap-2">
                {active && <active.Icon className="w-4.5 h-4.5" style={{ color: active.color }} />}
                <h2 className="text-[17px] font-semibold text-[#1a1d1a]">Écrire à {active?.label}</h2>
              </div>
              <div className="mt-5 space-y-3.5">
                <Input data-testid="contact-name-input" placeholder="Votre nom *" value={form.name} onChange={set('name')} className="h-11 rounded-xl" maxLength={80} />
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input data-testid="contact-email-input" type="email" placeholder="Votre email" value={form.email} onChange={set('email')} className="h-11 rounded-xl" />
                  <Input data-testid="contact-phone-input" placeholder="Votre téléphone (+225...)" value={form.phone} onChange={set('phone')} className="h-11 rounded-xl" inputMode="tel" />
                </div>
                <p className="text-[11.5px] text-neutral-500 -mt-1">Email ou téléphone : au moins l'un des deux pour vous répondre.</p>
                <Textarea data-testid="contact-message-input" rows={5} placeholder="Votre message *" value={form.message} onChange={set('message')} className="resize-none rounded-xl" maxLength={2000} />
              </div>
              <Button data-testid="contact-submit-btn" onClick={submit} disabled={sending} className="btn-anim mt-5 h-12 rounded-full px-6 bg-[#1a1d1a] hover:bg-black text-white text-[14px]">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Envoyer le message
              </Button>
            </>
          ) : (
            <div className="text-center py-6" data-testid="contact-success">
              <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center bg-[#5a8268]/15 text-[#5a8268]">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h2 className="mt-4 text-[20px] font-semibold text-[#1a1d1a]">Message envoyé !</h2>
              <p className="mt-2 text-[14px] text-neutral-600">Votre message a été transmis au service <b>{active?.label}</b>. Notre équipe vous répondra rapidement par email ou SMS.</p>
              <Button onClick={() => { setSent(false); setForm({ name: '', email: '', phone: '', message: '' }); }} variant="ghost" className="btn-anim mt-5 h-10 rounded-full px-5 text-neutral-700">Envoyer un autre message</Button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
